import React from 'react';
import PropTypes from 'prop-types';
import RecordActionBar from '../RecordActionBar';
import ActionDropdown from '../RecordActionBar/ActionDropdown';
import Button from '../Button';
import Tooltip from '../Tooltip';
import ChartActionButtons from '../chartActions/ChartActionButtons';
import {
  ArchiveSvg, ClipboardSvg, DownloadSvg, PaperPlaneTiltSvg,
  ArrowBendUpLeftSvg, PrinterSvg, NotificationSvg, LinkSimpleSvg,
  SquareSplitHorizontalSvg, NotePencilSvg, PencilSimpleSvg, DeviceMobileSvg,
} from '../../assets/img/redesign/svgPaths';
import { captureAnalytics } from '../../actions/preload';

const PRIMARY_LABELS = new Set(['Send', 'Chart Actions', 'Open in EMR', 'Make Copy', 'Archive', 'Unarchive']);
const CHART_LABELS = new Set(['Send to Chart', 'Create New Chart', 'Create Task', 'Create Note']);
const SHARED_ICONS = {
  Send: PaperPlaneTiltSvg,
  Archive: ArchiveSvg,
  Unarchive: ArchiveSvg,
  'Chart Actions': ClipboardSvg,
  Reply: ArrowBendUpLeftSvg,
  Print: PrinterSvg,
  Notify: NotificationSvg,
  'Secure Link': LinkSimpleSvg,
  'Publish Form': LinkSimpleSvg,
  'Split Record': SquareSplitHorizontalSvg,
  'Update Assigned Document': NotePencilSvg,
  'Update Referral Status': PencilSimpleSvg,
  'Send SMS': DeviceMobileSvg,
};

const PRIMARY_ORDER = ['Send', 'Chart Actions', 'Open in EMR', 'Download', 'Make Copy', 'Archive', 'Unarchive'];
const primaryRank = (action) => PRIMARY_ORDER.indexOf(action.text.startsWith('Download') ? 'Download' : action.text);

const renderAction = (action) => {
  if (action.type === 'chart-actions') {
    // This adapter forwards the chart-action descriptor and its group-level guard.
    // eslint-disable-next-line react/jsx-props-no-spreading
    return <ChartActionButtons key={action.text} {...action.chartActions} disabled={action.disabled} />;
  }
  const isDownload = action.type === 'dropdown' && action.text.startsWith('Download');
  let label = action.text;
  if (action.iconOnly) label = '';
  else if (isDownload) label = 'Download';
  const icon = isDownload ? DownloadSvg : SHARED_ICONS[label] || action.icon;
  if (action.type === 'dropdown') {
    return <ActionDropdown key={label} label={label} icon={icon} menu={action.menu} disabled={action.disabled} />;
  }
  return (
    <Tooltip key={label} title={action.toolTiptitle || ''}>
      <span>
        <Button
          type="tertiary"
          size="small"
          label={label}
          leftIcon={icon}
          disabled={action.disabled}
          aria-label={action.ariaLabel || label}
          title={action.tooltipTitle || action.toolTiptitle || label}
          className={action.iconOnly ? 'record-action-bar__icon-only' : undefined}
          data-tutorial={action.dataTutorial}
          onClick={(event) => {
            captureAnalytics('record_viewer_action_clicked', { surface: 'full_page', action: label });
            action.func(event);
          }}
        />
      </span>
    </Tooltip>
  );
};

// Adapt only the record viewer's descriptors; other headers keep their existing
// rendering. Visibility, disabled states and save continuations belong to the caller.
const HeaderActionBar = ({ buttons }) => {
  const shown = buttons.filter((button) => !button.hide);
  const legacyChartActions = shown.filter((button) => CHART_LABELS.has(button.text) && !button.keepSeparate);
  const actions = shown.filter((button) => !CHART_LABELS.has(button.text) || button.keepSeparate);
  if (legacyChartActions.length) {
    const send = legacyChartActions.find((button) => button.text === 'Send to Chart');
    const create = legacyChartActions.find((button) => button.text === 'Create New Chart');
    const task = legacyChartActions.find((button) => ['Create Task', 'Create Note'].includes(button.text));
    actions.splice(1, 0, {
      text: 'Chart Actions',
      type: 'chart-actions',
      chartActions: {
        canSendToChart: !!send,
        canCreateChart: !!create,
        canCreateTask: !!task,
        disableSendToChart: send?.disabled,
        disableCreateChart: create?.disabled,
        disableCreateTask: task?.disabled,
        onSendToChart: send?.func,
        onCreateChart: create?.func,
        onCreateTask: task?.func,
        createTaskLabel: task?.text,
      },
    });
  }

  const primary = [];
  const secondary = [];
  const navigation = [];
  actions.forEach((action) => {
    if (['Next Record', 'Previous Record', 'Comments'].includes(action.text)) navigation.push(action);
    else if (PRIMARY_LABELS.has(action.text) || action.text.startsWith('Download')) primary.push(action);
    else secondary.push(action);
  });
  primary.sort((a, b) => primaryRank(a) - primaryRank(b));

  return (
    <RecordActionBar
      surface="full_page"
      primary={primary.map(renderAction)}
      secondary={secondary.length ? secondary.map(renderAction) : null}
      navigation={navigation.map(renderAction)}
    />
  );
};

HeaderActionBar.propTypes = { buttons: PropTypes.arrayOf(PropTypes.object).isRequired };

export default HeaderActionBar;
